import { useEffect, useMemo, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Popover } from "@mui/material"
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material"
import moment from "moment"
import DateRangePicker from "components/date-range-picker"
import OPTooltip from "components/material/tooltip"
import { DATE, PERIOD, PERIOD_SELECTOR_LABELS } from "helpers/shapes"
import classnames from "classnames"
import PeriodSelectorOptions from "./options"
import { LoadingState, AdjustmentIndicator, PeriodDisplay } from "./components"
import { isDayBlocked, isMonthBlocked, isYearBlocked, computeAdjustedDates } from "./helpers"
import { getDate } from "modules/intraday/selectors/dates"
import { LOADING, DATA_STATUSES } from "helpers/data-statuses"

import "./style.scss"

const EMPTY_ARRAY = []

const DEFAULT_LABELS = {
  from: "Performance From",
  to: "As Of",
}

export default function PeriodSelector({
  startDate: initialStartDate,
  endDate: initialEndDate,
  period,
  earliestDate,
  latestDate,
  periodAdjusted,
  selectedDateAdjusted,
  datesWithLabels,
  customizeOptions,
  labels = DEFAULT_LABELS,
  unmodeledDates = EMPTY_ARRAY,
  onChange,
  handleResetOption = null,
  isIntradayEnabled = false,
  isIntradayBatchAvailable = false,
  isEndDateToday = false,
  intradayAsOf = null,
  intradayRelativeDay = "today",
  intradayBatchStatus = null,
  skipAutoAdjustment = false,
  tooltipContent,
  disablePortal = false,
}) {
  const [initialMomentStartDate, initialMomentEndDate] = useMemo(
    () => [moment(initialStartDate), moment(initialEndDate)],
    [initialStartDate, initialEndDate],
  )

  // In intraday mode, selection can run up to the browser-local "today" even when it is past the last
  // modeled date — so trading days in that gap (e.g. the server's current date in an ahead-of-server
  // timezone) stay selectable instead of being clamped to the last modeled date.
  const effectiveLatestDate = useMemo(() => {
    if (!isIntradayEnabled) {
      return latestDate
    }
    const localToday = getDate()
    return localToday > latestDate ? localToday : latestDate
  }, [isIntradayEnabled, latestDate])

  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(false)
  const [startDate, setStartDate] = useState(initialMomentStartDate)
  const [endDate, setEndDate] = useState(initialMomentEndDate)

  // We need to reset the dates when there's a change from above
  useEffect(() => {
    const adjusted = computeAdjustedDates(initialMomentStartDate, initialMomentEndDate, earliestDate, effectiveLatestDate, unmodeledDates, isIntradayEnabled, isIntradayBatchAvailable, isEndDateToday, skipAutoAdjustment)
    setStartDate(moment(adjusted.startDate || initialMomentStartDate))
    setEndDate(moment(adjusted.endDate || initialMomentEndDate))
  }, [initialMomentStartDate, initialMomentEndDate, earliestDate, effectiveLatestDate, unmodeledDates, isIntradayEnabled, isIntradayBatchAvailable, isEndDateToday, skipAutoAdjustment])

  const handleSelect = value => {
    if (value === "CUSTOM") {
      setCustom(true)
      setOpen(false)
    } else {
      onChange(value)
      setOpen(false)
    }
  }

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleCancel = () => {
    setCustom(false)
    setOpen(false)
    setStartDate(initialMomentStartDate)
    setEndDate(initialMomentEndDate)
  }

  const handleChange = (startDate, endDate) => {
    const formattedStartDate = startDate.format("YYYY-MM-DD")
    const formattedEndDate = endDate.format("YYYY-MM-DD")

    const change = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      adjusted: computeAdjustedDates(startDate, endDate, earliestDate, effectiveLatestDate, unmodeledDates, isIntradayEnabled, isIntradayBatchAvailable, isEndDateToday, false), // Always allow adjustment for user-selected custom dates
    }

    onChange(change)
    setCustom(false)
    setStartDate(moment(change.adjusted.startDate || change.startDate))
    setEndDate(moment(change.adjusted.endDate || change.endDate))
  }

  const handleClickAway = () => {
    setOpen(false)
  }

  const handleIsDateBlocked = useCallback(date => isDayBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, unmodeledDates, effectiveLatestDate, earliestDate), [isIntradayEnabled, isIntradayBatchAvailable, unmodeledDates, effectiveLatestDate, earliestDate])
  const handleIsMonthBlocked = useCallback(date => isMonthBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, effectiveLatestDate, earliestDate), [isIntradayEnabled, isIntradayBatchAvailable, effectiveLatestDate, earliestDate])
  const handleIsYearBlocked = useCallback(date => isYearBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, effectiveLatestDate, earliestDate), [isIntradayEnabled, isIntradayBatchAvailable, effectiveLatestDate, earliestDate])

  if (period.startDate) {
    period = "CUSTOM"
  }

  if (isIntradayEnabled && intradayBatchStatus === LOADING) {
    return (
      <LoadingState
        periodAdjusted={periodAdjusted}
        selectedDateAdjusted={selectedDateAdjusted}
      />
    )
  }

  const periodDisplayContent = <a className="link" ref={setAnchorEl} onClick={handleToggle}>
      <AdjustmentIndicator
        periodAdjusted={periodAdjusted}
        selectedDateAdjusted={selectedDateAdjusted}
      />
      <PeriodDisplay
        startDate={startDate}
        endDate={endDate}
        datesWithLabels={datesWithLabels}
        labels={labels}
      />
      {open ? <ArrowDropUp /> : <ArrowDropDown />}
  </a>

  const periodDisplay = tooltipContent
    ? <OPTooltip title={tooltipContent} enterDelay={1000} placement="top">{periodDisplayContent}</OPTooltip>
    : periodDisplayContent

  return (
    <div
      className={classnames("components_period-selector", {
        adjusted: periodAdjusted || selectedDateAdjusted,
      })}
    >
      {!custom && periodDisplay}
      {custom && <DateRangePicker {...{
        earliestDate,
        latestDate: effectiveLatestDate,
        startDate,
        endDate,
        cancel: handleCancel,
        setDates: handleChange,
        isDayBlocked: handleIsDateBlocked,
        isMonthBlocked: handleIsMonthBlocked,
        isYearBlocked: handleIsYearBlocked,
      }}
      />}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClickAway}
        disablePortal={disablePortal}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { elevation: 0, className: "period-selector-popover-paper" } }}
      >
        <PeriodSelectorOptions {...{
          period,
          customizeOptions,
          handleClickAway,
          handleSelect,
          handleResetOption,
          endDate,
          latestDate,
          isIntradayEnabled,
          isIntradayBatchAvailable,
          isEndDateToday,
          intradayAsOf,
          intradayRelativeDay,
        }}
        />
      </Popover>
    </div>
  )
}

PeriodSelector.propTypes = {
  startDate: DATE.isRequired,
  endDate: DATE.isRequired,
  earliestDate: DATE.isRequired,
  latestDate: DATE.isRequired,
  onChange: PropTypes.func.isRequired,
  period: PERIOD.isRequired,
  periodAdjusted: PropTypes.bool,
  selectedDateAdjusted: PropTypes.bool,
  datesWithLabels: PropTypes.bool,
  customizeOptions: PropTypes.func,
  labels: PERIOD_SELECTOR_LABELS,
  unmodeledDates: PropTypes.arrayOf(PropTypes.string),
  handleResetOption: PropTypes.func,
  isIntradayEnabled: PropTypes.bool,
  isIntradayBatchAvailable: PropTypes.bool,
  isEndDateToday: PropTypes.bool,
  intradayAsOf: PropTypes.string,
  intradayRelativeDay: PropTypes.string,
  intradayBatchStatus: PropTypes.oneOf(DATA_STATUSES),
  skipAutoAdjustment: PropTypes.bool,
  tooltipContent: PropTypes.string,
  disablePortal: PropTypes.bool,
}
